// @ts-nocheck
/**
 * 質屋システム
 * 差し押さえられたカードの販売管理
 */

// ========================================
// 質屋NPC データ
// ========================================

export const GORUDO = {
  name: 'ゴルド',
  icon: '💰',
  description: '太った商人、金歯、そろばん、油っぽい笑顔',
  personality: 'がめつい、計算高い、でも商売は正直',

  // 台詞
  quotes: {
    greeting: [
      'へっへっへ、いらっしゃい',
      '今日もいい品が入ってるよ',
      '誰かの不幸は誰かの幸福ってね',
      '掘り出し物があるよ',
    ],
    purchase: [
      'まいどあり',
      '大事にしてやってくれよ',
      '元の持ち主には...内緒だよ',
      'へっへ、いい買い物だね',
    ],
    empty: [
      '今日は品薄だねぇ',
      'みんな借金返してるのかな？つまらないねぇ',
      '商売あがったりだよ',
      'また来な、品物入ったら声かけるよ',
    ],
    browse: [
      'ゆっくり見ていきな',
      'どれも掘り出し物だよ',
      '市場価格より安いよ、へっへっへ',
    ],
  },
};

/**
 * ランダムな台詞を取得
 * @param {string} category - カテゴリ（greeting, purchase, empty, browse）
 * @returns {string} 台詞
 */
export function getGorudoQuote(category) {
  const quotes = GORUDO.quotes[category] || GORUDO.quotes.greeting;
  return quotes[Math.floor(Math.random() * quotes.length)];
}

// ========================================
// 質屋在庫管理
// ========================================

/**
 * 質屋の初期状態を作成
 * @returns {Object} 初期質屋状態
 */
export function createInitialPawnShopState() {
  return {
    inventory: [], // 在庫カード
    totalSold: 0, // 累計販売数
    totalRevenue: 0, // 累計売上
    lastRestock: null, // 最後の入荷日
  };
}

/**
 * 差し押さえカードを質屋に追加
 * @param {Object} pawnShopData - 質屋データ
 * @param {Array} pawnItems - 差し押さえアイテム
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @returns {Object} 更新された質屋データ
 */
export function addToPawnShop(pawnShopData, pawnItems, currentBattleNumber) {
  const newInventory = [
    ...pawnShopData.inventory,
    ...pawnItems.map((item, index) => ({
      ...item,
      pawnId: `${currentBattleNumber}-${Date.now()}-${index}`, // 一意のID
      addedAt: currentBattleNumber,
    })),
  ];

  return {
    ...pawnShopData,
    inventory: newInventory,
    lastRestock: currentBattleNumber,
  };
}

/**
 * 質屋から購入
 * @param {Object} pawnShopData - 質屋データ
 * @param {string} pawnId - 購入するアイテムのID
 * @param {number} playerGold - プレイヤーの所持金
 * @returns {Object} { success, error, item, newPawnShopData, cost }
 */
export function purchaseFromPawnShop(pawnShopData, pawnId, playerGold) {
  const itemIndex = pawnShopData.inventory.findIndex(item => item.pawnId === pawnId);

  if (itemIndex === -1) {
    return {
      success: false,
      error: 'アイテムが見つかりません',
      item: null,
      newPawnShopData: pawnShopData,
      cost: 0,
    };
  }

  const item = pawnShopData.inventory[itemIndex];
  const cost = item.pawnPrice;

  if (playerGold < cost) {
    return {
      success: false,
      error: '所持金が不足しています',
      item: null,
      newPawnShopData: pawnShopData,
      cost,
    };
  }

  // 在庫から削除
  const newInventory = [
    ...pawnShopData.inventory.slice(0, itemIndex),
    ...pawnShopData.inventory.slice(itemIndex + 1),
  ];

  return {
    success: true,
    error: null,
    item: {
      cardId: item.cardId,
      rarity: item.rarity,
      name: item.name,
      attribute: item.attribute,
    },
    newPawnShopData: {
      ...pawnShopData,
      inventory: newInventory,
      totalSold: pawnShopData.totalSold + 1,
      totalRevenue: pawnShopData.totalRevenue + cost,
    },
    cost,
  };
}

// ========================================
// 在庫検索・フィルタリング
// ========================================

/**
 * 質屋在庫をフィルタリング
 * @param {Object} pawnShopData - 質屋データ
 * @param {Object} filters - フィルター条件
 * @returns {Array} フィルタリングされた在庫
 */
export function filterInventory(pawnShopData, filters = {}) {
  let inventory = [...pawnShopData.inventory];

  // レアリティフィルター
  if (filters.rarity) {
    inventory = inventory.filter(item => item.rarity === filters.rarity);
  }

  // 属性フィルター
  if (filters.attribute) {
    inventory = inventory.filter(item => item.attribute === filters.attribute);
  }

  // 価格範囲フィルター
  if (filters.minPrice !== undefined) {
    inventory = inventory.filter(item => item.pawnPrice >= filters.minPrice);
  }
  if (filters.maxPrice !== undefined) {
    inventory = inventory.filter(item => item.pawnPrice <= filters.maxPrice);
  }

  return inventory;
}

/**
 * 質屋在庫をソート
 * @param {Array} inventory - 在庫
 * @param {string} sortBy - ソートキー（price, rarity, name, addedAt）
 * @param {boolean} ascending - 昇順か
 * @returns {Array} ソートされた在庫
 */
export function sortInventory(inventory, sortBy = 'price', ascending = true) {
  const rarityOrder = ['GR', 'UR', 'SR', 'R', 'UC', 'C'];

  const sorted = [...inventory].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'price':
        comparison = a.pawnPrice - b.pawnPrice;
        break;
      case 'rarity':
        comparison = rarityOrder.indexOf(a.rarity) - rarityOrder.indexOf(b.rarity);
        break;
      case 'name':
        comparison = (a.name || '').localeCompare(b.name || '');
        break;
      case 'addedAt':
        comparison = (a.addedAt || 0) - (b.addedAt || 0);
        break;
      default:
        comparison = 0;
    }

    return ascending ? comparison : -comparison;
  });

  return sorted;
}

// ========================================
// UI ヘルパー
// ========================================

/**
 * 質屋のサマリーを取得
 * @param {Object} pawnShopData - 質屋データ
 * @returns {Object} サマリー
 */
export function getPawnShopSummary(pawnShopData) {
  const inventory = pawnShopData.inventory || [];

  // レアリティ別集計
  const byRarity = {};
  for (const item of inventory) {
    byRarity[item.rarity] = (byRarity[item.rarity] || 0) + 1;
  }

  // 属性別集計
  const byAttribute = {};
  for (const item of inventory) {
    const attr = item.attribute || 'なし';
    byAttribute[attr] = (byAttribute[attr] || 0) + 1;
  }

  // 価格統計
  const prices = inventory.map(item => item.pawnPrice);
  const totalValue = prices.reduce((sum, p) => sum + p, 0);
  const avgPrice = prices.length > 0 ? totalValue / prices.length : 0;
  const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
  const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

  return {
    totalItems: inventory.length,
    byRarity,
    byAttribute,
    totalValue,
    avgPrice: Math.floor(avgPrice),
    minPrice,
    maxPrice,
    hasItems: inventory.length > 0,
    totalSold: pawnShopData.totalSold || 0,
    totalRevenue: pawnShopData.totalRevenue || 0,
  };
}

/**
 * 市場価格との比較を計算
 * @param {Object} pawnItem - 質屋アイテム
 * @param {number} marketPrice - 現在の市場価格
 * @returns {Object} { discount, discountPercent }
 */
export function calculateDiscount(pawnItem, marketPrice) {
  const pawnPrice = pawnItem.pawnPrice;
  const discount = marketPrice - pawnPrice;
  const discountPercent = marketPrice > 0
    ? Math.floor((discount / marketPrice) * 100)
    : 0;

  return {
    discount,
    discountPercent,
    isGoodDeal: discount > 0,
  };
}

// ========================================
// 買い戻し機能
// ========================================

/**
 * プレイヤーの元の所持カードが質屋にあるかチェック
 * @param {Object} pawnShopData - 質屋データ
 * @param {string} cardId - カードID
 * @returns {Array} 該当アイテム
 */
export function findPlayerCards(pawnShopData, cardId) {
  return pawnShopData.inventory.filter(item => item.cardId === cardId);
}

/**
 * 質屋から全て購入
 * @param {Object} pawnShopData - 質屋データ
 * @param {number} playerGold - プレイヤーの所持金
 * @returns {Object} { success, error, items, newPawnShopData, totalCost }
 */
export function purchaseAllFromPawnShop(pawnShopData, playerGold) {
  const inventory = pawnShopData.inventory || [];

  if (inventory.length === 0) {
    return {
      success: false,
      error: '在庫がありません',
      items: [],
      newPawnShopData: pawnShopData,
      totalCost: 0,
    };
  }

  const totalCost = inventory.reduce((sum, item) => sum + item.pawnPrice, 0);

  if (playerGold < totalCost) {
    return {
      success: false,
      error: `所持金が不足しています（必要: ${totalCost.toLocaleString()}G）`,
      items: [],
      newPawnShopData: pawnShopData,
      totalCost,
    };
  }

  // 購入アイテムリストを作成
  const purchasedItems = inventory.map(item => ({
    cardId: item.cardId,
    rarity: item.rarity,
    name: item.name,
    attribute: item.attribute,
  }));

  return {
    success: true,
    error: null,
    items: purchasedItems,
    newPawnShopData: {
      ...pawnShopData,
      inventory: [],
      totalSold: pawnShopData.totalSold + inventory.length,
      totalRevenue: pawnShopData.totalRevenue + totalCost,
    },
    totalCost,
  };
}

/**
 * 在庫があるかチェック
 * @param {Object} pawnShopData - 質屋データ
 * @returns {boolean} 在庫があるか
 */
export function hasInventory(pawnShopData) {
  return pawnShopData.inventory && pawnShopData.inventory.length > 0;
}

// ========================================
// エクスポート
// ========================================

export default {
  GORUDO,
  getGorudoQuote,
  createInitialPawnShopState,
  addToPawnShop,
  purchaseFromPawnShop,
  purchaseAllFromPawnShop,
  filterInventory,
  sortInventory,
  getPawnShopSummary,
  calculateDiscount,
  findPlayerCards,
  hasInventory,
};
